"use client"

import type { Omit } from "@wagmi/core/internal"
import { type Config, useChainId, useConfig } from "wagmi"

import { Evaluate } from "viem"
import type { GetPerspectiveErrorType } from "../actions/getPerspective"
import {
  type GetPerspectiveData,
  type GetPerspectiveOptions,
  type GetPerspectiveQueryFnData,
  type GetPerspectiveQueryKey,
  getPerspectiveQueryOptions,
} from "../query"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
  UseQueryReturnType,
} from "../types"
import { useQuery } from "../utils"

export type UsePerspectiveParameters<
  config extends Config = Config,
  selectData = GetPerspectiveData,
> = Evaluate<
  GetPerspectiveOptions &
    ConfigParameter<config> & {
      query?:
        | Evaluate<
            Omit<
              UseQueryParameters<
                GetPerspectiveQueryFnData,
                GetPerspectiveErrorType,
                selectData,
                GetPerspectiveQueryKey
              >,
              "gcTime" | "staleTime"
            >
          >
        | undefined
      select?: (data: GetPerspectiveQueryFnData) => selectData
    }
>

export type UsePerspectiveReturnType<selectData = GetPerspectiveData> =
  UseQueryReturnType<selectData, GetPerspectiveErrorType>

export function usePerspective<
  config extends ExtendedConfig,
  selectData = GetPerspectiveData,
>(
  parameters: UsePerspectiveParameters<config, selectData> = {},
): UsePerspectiveReturnType<selectData> {
  const { query = {}, address, evcAddress, scopeKey } = parameters

  const config = useConfig(parameters)
  const chainId = useChainId({ config })

  const { queryKey, ...options } = getPerspectiveQueryOptions(config, {
    address,
    chainId: parameters.chainId ?? chainId,
    evcAddress,
    scopeKey,
  })
  const enabled = Boolean(address && (query.enabled ?? true))

  return useQuery({
    ...query,
    ...options,
    queryKey,
    enabled,
    refetchOnMount: false,
    retry: false,
  })
}
